import { isExpressionValue } from '../helpers/expression-factory.helpers';
import { hasTruthyProperties } from '../helpers/object.helpers';
import { isSupportedProductElementUnit } from '../helpers/product-element.helpers';
import { ProductRecordOwners } from '../helpers/product.helpers';
import { getBuildingVersionFromProductRecordOwners } from '../helpers/recursive-element.helpers';
import { isSelectableQuantityUnit } from '../helpers/unit.helpers';
import { IProductElement } from '../models/project.model';
import { selectableQuantityUnits } from '../models/unit.model';
import { isValidGenericProductId } from './product-validation';
import { ProjectValidationErrors } from './project-validation';
import { ValidationResult, ElementValidationError } from './validation.models';

/**
 * Check that a IProductElement is valid
 * @param element
 * @returns true if valid, error message if invalid
 */
export const isValidProductElement = (
  element: IProductElement,
  productRecordOwner: ProductRecordOwners,
): ValidationResult => {
  const { count, unit, generic_product_id, product_id } = element;

  // Only productElements should have a valid count
  if (!isExpressionValue(count)) {
    return new ElementValidationError(ProjectValidationErrors.INVALID_COUNT, {
      element,
    });
  }
  if (!isSelectableQuantityUnit(unit)) {
    return new ElementValidationError(
      `Invalid productElement unit "${String(unit)}", expected one of ${selectableQuantityUnits.join(', ')}`,
      { element },
    );
  }
  if (!hasTruthyProperties(element, 'product_id')) {
    return new ElementValidationError(
      ProjectValidationErrors.PRODUCT_ELEMENT_MISSING_ID_OR_PRODUCT_ID,
      { element },
    );
  }
  if (!product_id) {
    return new ElementValidationError(
      'Product element product reference is required',
      { element },
    );
  }
  if (generic_product_id) {
    const validId = isValidGenericProductId(generic_product_id);
    if (validId !== true) {
      return validId;
    }
    // Generic id always comes with a product_id
    if (!product_id) {
      return new ElementValidationError(
        'Product_id must be set if generic_product_id is set',
        { element },
      );
    }
  }

  const version = getBuildingVersionFromProductRecordOwners(productRecordOwner);

  // If version is defined, we can validate the connection to the product as well (only when validating the entire building version)
  if (version) {
    const versionProducts = version.products;
    if (
      // Make sure unit exists in the conversion factors of select products
      !isSupportedProductElementUnit(versionProducts, element)
    ) {
      // TODO: Fix https://nodon.atlassian.net/browse/DEC-1800 and migrate then turn this on
      console.warn(
        `Invalid productElement unit "${String(unit)}". It doesn't exist in the conversion factors of select products`,
      );
    }
  }
  return true;
};
